import { FormControl, FormHelperText } from '@mui/material';
import { useRef, useMemo, useState, useCallback } from 'react';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import {
  getIn,
  setIn,
  useField,
  FieldArray,
  useFormikContext,
  type FormikValues,
} from 'formik';
import {
  useSensor,
  DndContext,
  useSensors,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';

import { useAlert } from 'hooks';
import { AddButton } from 'components';
import { controlError } from 'utils/error';
import { fieldTypeTitle, getDefaultField } from 'utils/form';
import { FormField, FormFieldType, FormFieldTypeTitle } from 'types';

import { FieldsProps } from './types';
import { FieldsContainer } from './fields-container';
import { DragPreviewCard } from './drag-preview-card';
import { EMPTY_OUTCOME_FIELD_SOURCES } from './config';
import { useSharedFieldDnDContext } from './dnd-context';
import { StaticFieldsContainer } from './static-fields-container';
import {
  isFieldItemId,
  getFieldItemId,
  moveFieldBetweenContainers,
} from './dnd';
import {
  cloneFieldForPaste,
  readBuilderClipboard,
  buildClipboardPayload,
  writeBuilderClipboard,
  createOutcomeSourceKeys,
  findFirstIncompatibleFieldType,
} from './clipboard';

export const Fields = ({
  name,
  fields,
  simplified = false,
  enableSorting = true,
  allowInputId = false,
  allowOutcomes = false,
  allowPageBreak = true,
  allowFieldArray = true,
  allowOutcomeFields = false,
  allowReferenceCodes = false,
  outcomeFieldSources = EMPTY_OUTCOME_FIELD_SOURCES,
}: FieldsProps) => {
  const [, meta] = useField(name);
  const { values, setValues } = useFormikContext<FormikValues>();
  const { showErrorAlert, showSuccessAlert } = useAlert();
  const valuesRef = useRef(values);
  const isPastingRef = useRef(false);
  valuesRef.current = values;
  const sharedDnD = useSharedFieldDnDContext();
  const [localActiveFieldId, setLocalActiveFieldId] = useState<string | null>(
    null
  );
  const [localActiveFieldWidth, setLocalActiveFieldWidth] = useState<
    number | null
  >(null);
  const errorText = controlError(meta, name, 'Fields');
  const hasError = Boolean(errorText);
  const isSharedContainer = Boolean(
    enableSorting && sharedDnD?.containerIds.includes(name)
  );
  const localSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const compatibilityRules = useMemo(
    () => ({
      allowOutcomes,
      allowPageBreak,
      allowFieldArray,
      allowOutcomeFields,
    }),
    [allowOutcomes, allowFieldArray, allowPageBreak, allowOutcomeFields]
  );
  const outcomeSourceKeys = useMemo(
    () => createOutcomeSourceKeys(outcomeFieldSources),
    [outcomeFieldSources]
  );

  const handleLocalDragStart = useCallback(({ active }: DragStartEvent) => {
    if (
      typeof active.id !== 'string' ||
      !isFieldItemId(active.id) ||
      typeof active.data.current?.sortable?.index !== 'number'
    ) {
      return;
    }

    setLocalActiveFieldId(active.id);
    setLocalActiveFieldWidth(active.rect.current.initial?.width ?? null);
  }, []);

  const handleLocalDragEnd = useCallback(
    ({ over, active }: DragEndEvent) => {
      setLocalActiveFieldId(null);
      setLocalActiveFieldWidth(null);

      if (
        !over ||
        typeof active.id !== 'string' ||
        typeof over.id !== 'string' ||
        active.id === over.id
      ) {
        return;
      }

      setValues(
        moveFieldBetweenContainers({
          sourcePath: name,
          targetPath: name,
          overItemId: over.id,
          activeItemId: active.id,
          values: valuesRef.current,
        })
      );
    },
    [name, setValues]
  );

  const handleLocalDragCancel = useCallback(() => {
    setLocalActiveFieldId(null);
    setLocalActiveFieldWidth(null);
  }, []);

  const handleCopyField = useCallback(
    async (field: FormField) => {
      try {
        await writeBuilderClipboard(buildClipboardPayload('field', field));
        showSuccessAlert('Field copied');
      } catch (error) {
        showErrorAlert(
          error instanceof Error ? error.message : 'Failed to copy field'
        );
      }
    },
    [showErrorAlert, showSuccessAlert]
  );

  const handlePasteFieldAfter = useCallback(
    async (index: number) => {
      if (isPastingRef.current) return;
      isPastingRef.current = true;
      try {
        const payload = await readBuilderClipboard();
        if (payload.entityType !== 'field') {
          showErrorAlert('Clipboard does not contain a field');
          return;
        }

        const sourceField = payload.data as FormField;
        const incompatibleType = findFirstIncompatibleFieldType(
          sourceField,
          compatibilityRules
        );
        if (incompatibleType) {
          showErrorAlert(
            `Cannot paste field with unsupported type "${fieldTypeTitle(
              incompatibleType
            )}" in this container`
          );
          return;
        }

        const targetFields = (getIn(valuesRef.current, name) ??
          []) as FormField[];
        const nextFields = [...targetFields];
        nextFields.splice(
          index + 1,
          0,
          cloneFieldForPaste(sourceField, outcomeSourceKeys)
        );

        setValues(setIn(valuesRef.current, name, nextFields));
        showSuccessAlert('Field pasted');
      } catch (error) {
        showErrorAlert(
          error instanceof Error ? error.message : 'Failed to paste field'
        );
      } finally {
        isPastingRef.current = false;
      }
    },
    [
      name,
      setValues,
      showErrorAlert,
      showSuccessAlert,
      compatibilityRules,
      outcomeSourceKeys,
    ]
  );

  const renderContent = ({
    push,
    remove,
  }: {
    remove: (index: number) => void;
    push: (value: FormField) => void;
  }) => (
    <div className="flex flex-col gap-4">
      {enableSorting ? (
        <FieldsContainer
          name={name}
          fields={fields}
          onRemove={remove}
          simplified={simplified}
          allowInputId={allowInputId}
          onCopyField={handleCopyField}
          enableSorting={enableSorting}
          allowOutcomes={allowOutcomes}
          allowPageBreak={allowPageBreak}
          allowFieldArray={allowFieldArray}
          allowOutcomeFields={allowOutcomeFields}
          allowReferenceCodes={allowReferenceCodes}
          onPasteFieldAfter={handlePasteFieldAfter}
          outcomeFieldSources={outcomeFieldSources}
          activeFieldId={
            isSharedContainer
              ? (sharedDnD?.activeFieldId ?? null)
              : localActiveFieldId
          }
        />
      ) : (
        <StaticFieldsContainer
          name={name}
          fields={fields}
          onRemove={remove}
          simplified={simplified}
          allowInputId={allowInputId}
          onCopyField={handleCopyField}
          enableSorting={enableSorting}
          allowOutcomes={allowOutcomes}
          allowPageBreak={allowPageBreak}
          allowFieldArray={allowFieldArray}
          allowOutcomeFields={allowOutcomeFields}
          allowReferenceCodes={allowReferenceCodes}
          onPasteFieldAfter={handlePasteFieldAfter}
          outcomeFieldSources={outcomeFieldSources}
        />
      )}

      {hasError && !simplified && (
        <FormHelperText error className="!ml-0">
          {errorText}
        </FormHelperText>
      )}

      {!simplified && (
        <AddButton title="field" onClick={() => push(getDefaultField())} />
      )}
    </div>
  );

  return (
    <FormControl fullWidth error={hasError}>
      <FieldArray
        name={name}
        render={({ push, remove }) => {
          if (!enableSorting) {
            return renderContent({ push, remove });
          }

          if (isSharedContainer) {
            return renderContent({ push, remove });
          }

          const activeField = localActiveFieldId
            ? fields.find((f) => getFieldItemId(f.id) === localActiveFieldId)
            : undefined;

          return (
            <DndContext
              sensors={localSensors}
              onDragEnd={handleLocalDragEnd}
              onDragStart={handleLocalDragStart}
              onDragCancel={handleLocalDragCancel}
            >
              {renderContent({ push, remove })}
              <DragOverlay>
                {activeField ? (
                  <DragPreviewCard
                    showHandle
                    width={localActiveFieldWidth}
                    title={FormFieldTypeTitle[activeField.type]}
                    subtitle={
                      activeField.label?.trim() ||
                      (activeField.type === FormFieldType.FIELD_ARRAY
                        ? undefined
                        : 'Untitled field')
                    }
                  />
                ) : null}
              </DragOverlay>
            </DndContext>
          );
        }}
      />
    </FormControl>
  );
};
